import { useEffect, useRef, useState } from 'react';
import { Map } from 'google-maps-react-markers';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import { isInSoftBlockMode } from 'constants/geoBlocking';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useInView } from 'hooks/useInView';
import FilterIcon from 'svg/FilterIcon';
import Button from 'components/Button';
import MapToggleBar from 'components/Explore/MapToggleBar';
import GoogleMap from 'components/GoogleMap';
import { useModal } from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import AddressBar from './AddressBar';
import DistanceDropdown from './DistanceDropdown';
import FilterModal from './FilterModal';
import Tabs from './Tabs';
import { DiscoverPageProps, ExploreAllMarketPlacesTypesEnum } from './props';
import { tabs } from './utils';

const DiscoverPage: React.FC<DiscoverPageProps> = ({
  filterProps,
  mapProps,
  distance,
  setDistance,
  List,
  handleLocationRetrieval,
  isTabInsidePage = false,
  tabUpdate,
  showTabs = true,
  showAddressBar = true,
  extraHeaderContent,
  onAddressChange,
  onMapToggle,
}) => {
  const mapRef = useRef<Map>();
  const { closeModal, openModal, isOpen: isModalOpen } = useModal();
  const discoverContainerRef = useRef<HTMLDivElement | null>(null);
  const [isOpenMap, setIsOpenMap] = useState<boolean>(false);
  const { position, getEstimatedLocation, requestUserLocation, hasLocationPermission } =
    useGeoLocation();

  const router = useRouter();

  const isDiscoverSectionInView = useInView(discoverContainerRef);

  const getInitialTabIndex = (): number => {
    const currentPath = router.pathname.split('/').pop();
    const tabIndex = tabs.findIndex((tab) => tab.value === currentPath);

    return tabIndex !== -1 ? tabIndex : 0;
  };

  const [activeTabIndex, setActiveTabIndex] = useState<number>(getInitialTabIndex());

  const handleTabChange = (newTabIndex: number) => {
    setActiveTabIndex(newTabIndex);
    if (tabUpdate && isTabInsidePage) tabUpdate(newTabIndex);
  };

  const handleGoogleApiLoaded = (map: Map) => {
    mapRef.current = map;
  };

  const handleToggleMap = () => {
    setIsOpenMap((curr) => {
      const next = !curr;
      onMapToggle?.(next);
      return next;
    });
  };

  useEffect(() => {
    if (discoverContainerRef.current && isOpenMap)
      discoverContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
  }, [isOpenMap]);

  useEffect(() => {
    const getLocation = async () => {
      try {
        await hasLocationPermission();
        await requestUserLocation();
      } catch (error) {
        await getEstimatedLocation();
      } finally {
        handleLocationRetrieval?.();
      }
    };

    if (!position) {
      getLocation();
    } else {
      handleLocationRetrieval?.();
    }
  }, [position]);

  return (
    <>
      <div
        ref={discoverContainerRef}
        className={classNames(
          'relative grid w-full grid-cols-[minmax(0,1fr)_45%]',
          extraHeaderContent ? 'grid-rows-[auto_auto_1fr]' : 'grid-rows-[auto_1fr]',
          isOpenMap
            ? extraHeaderContent
              ? "[grid-template-areas:'header_map''extra-header_map''list_map'] max-lg:h-[calc(100vh-theme(height.topnav))] max-lg:[grid-template-areas:'header_header''extra-header_extra-header''map_map']"
              : "[grid-template-areas:'header_map''list_map'] max-lg:h-[calc(100vh-theme(height.topnav))] max-lg:[grid-template-areas:'header_header''map_map']"
            : extraHeaderContent
            ? "h-full max-lg:[grid-template-areas:'header_header''extra-header_extra-header''list_list'] lg:min-h-[calc(100vh-theme(height.topnav))] lg:[grid-template-areas:'header_map''extra-header_map''list_map']"
            : "h-full max-lg:[grid-template-areas:'header_header''list_list'] lg:min-h-[calc(100vh-theme(height.topnav))] lg:[grid-template-areas:'header_map''list_map']",
        )}
      >
        {(showAddressBar || distance || showTabs) && (
          <div
            className={classNames(
              'sticky top-topnav z-10 flex flex-col gap-4 bg-color-bg-lightmode-primary p-4 dark:bg-color-bg-darkmode-primary',
              '[grid-area:header]',
            )}
          >
            <div className="flex items-center gap-6">
              {showAddressBar && <AddressBar onAddressChange={onAddressChange} />}
              {distance && setDistance && (
                <DistanceDropdown
                  distance={distance}
                  handleDistanceChange={(distance) => setDistance(distance)}
                  activeTabValue={tabs[activeTabIndex].value as ExploreAllMarketPlacesTypesEnum}
                />
              )}
            </div>
            {showTabs && (
              <div className="flex items-center justify-between gap-2">
                <div className="w-full min-w-0 max-w-[40.645rem]">
                  <Tabs
                    tabs={tabs}
                    activeTabIndex={activeTabIndex}
                    onTabChange={handleTabChange}
                    isTabInsidePage={isTabInsidePage}
                  />
                </div>
                {filterProps && (
                  <Button
                    onClick={() => openModal()}
                    variant="inverted"
                    className="max-w-32 shrink max-sm:hidden"
                    size="md"
                    iconLeft={
                      <FilterIcon className="h-4 w-4 text-color-bg-lightmode-invert dark:text-color-bg-darkmode-invert" />
                    }
                  >
                    Filters
                  </Button>
                )}
              </div>
            )}
            {/* Custom header content (breadcrumbs, categories, etc.) */}
          </div>
        )}

        {/* Extra header content section */}
        {extraHeaderContent && (
          <div className="p-4 pt-0 [grid-area:extra-header]">{extraHeaderContent}</div>
        )}

        {/* List section */}
        <div
          className={classNames(
            'flex min-w-0 flex-col overflow-auto',
            '[grid-area:list]',
            isOpenMap ? 'hidden lg:flex' : 'flex',
          )}
        >
          {List}
        </div>

        {/* Map section */}
        <div
          className={classNames(
            'sticky [grid-area:map]',
            isOpenMap
              ? 'block lg:top-[theme(height.topnav)] lg:h-[calc(100vh-theme(height.topnav))]'
              : 'max-lg:hidden lg:top-[theme(height.topnav)] lg:h-[calc(100vh-theme(height.topnav))]',
          )}
        >
          <GoogleMap
            {...mapProps}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              disableDefaultUI: true,
              clickableIcons: false,
            }}
            onGoogleApiLoaded={handleGoogleApiLoaded}
            softBlockPlaceholder={isInSoftBlockMode()}
          />
        </div>
      </div>

      {/* Filter Modal */}
      {filterProps && (
        <FilterModal
          title={filterProps.title}
          isOpen={isModalOpen}
          closeModal={closeModal}
          clearAll={filterProps.clearAll}
          closeButtonText={filterProps.closeButtonText}
        >
          {filterProps.children}
        </FilterModal>
      )}

      {/* Map toggle Button */}
      <MapToggleBar
        isOpenMap={isOpenMap}
        onToggleMap={handleToggleMap}
        isInView={isDiscoverSectionInView}
        onOpenFilters={filterProps ? () => openModal() : undefined}
      />
    </>
  );
};

export default DiscoverPage;
